import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Extract locale from URL path (e.g., /zh-Hans/getinvolved/ -> zh-hans)
 * Handles paths with or without trailing slashes (e.g., /en or /en/)
 */
const getLocaleFromPath = () => {
  const match = /^\/([a-z]{2,3}(?:-[a-z0-9-]+)?)(?:\/|$)/i.exec(window.location.pathname)
  return match ? match[1].toLowerCase() : null
}

/**
 * LanguageSyncProvider ensures i18n language matches the URL locale before rendering children.
 *
 * This fixes a race condition where:
 * 1. When switching languages via the language selector, the URL changes (e.g., /en/ -> /it/)
 * 2. The page reloads with the new locale in the URL
 * 3. But i18n initializes BEFORE setAppLocales() adds non-baseLocale languages
 * 4. Without the supported languages configured, i18n defaults to English
 * 5. Components render with English translations initially
 * 6. On a second page reload, translations are cached and work correctly
 *
 * This provider waits for the language to sync with the URL before rendering,
 * preventing the flash of English content on initial load.
 */
const LanguageSyncProvider = ({ children }) => {
  const { i18n } = useTranslation(undefined, { bindI18n: 'languageChanged loaded' })
  const [isLanguageSynced, setIsLanguageSynced] = useState(false)
  const [currentPathname] = useState(() => window.location.pathname)

  useEffect(() => {
    const urlLocale = getLocaleFromPath()

    const tryChangeLanguage = () => {
      if (urlLocale && i18n.language !== urlLocale && i18n.options.supportedLngs?.includes(urlLocale)) {
        i18n
          .changeLanguage(urlLocale)
          .then(() => {
            setIsLanguageSynced(true)
          })
          .catch((error) => {
            // If language change fails, log error but allow rendering to prevent blocking
            console.error('[LanguageSyncProvider] Failed to change language:', error)
            setIsLanguageSynced(true)
          })
        return true
      }
      return false
    }

    // If no URL locale or already matching, we're synced
    if (!urlLocale || i18n.language === urlLocale) {
      setIsLanguageSynced(true)
      return undefined
    }

    // Try immediately - if successful, we're done
    if (tryChangeLanguage()) {
      return undefined
    }

    // Language not yet in supportedLngs - listen for when translations load
    // This handles the race condition where setAppLocales hasn't run yet
    const handleLoaded = () => {
      if (tryChangeLanguage()) {
        i18n.off('loaded', handleLoaded)
      }
    }

    i18n.on('loaded', handleLoaded)

    // Also poll periodically to catch cases where the 'loaded' event might not fire
    // This is especially important for Asian languages (Korean, Japanese) on production
    // Using 500ms interval to balance responsiveness with performance
    const pollInterval = setInterval(() => {
      if (tryChangeLanguage()) {
        clearInterval(pollInterval)
        i18n.off('loaded', handleLoaded)
      }
    }, 500)

    // Set a timeout fallback to avoid blocking forever if language never becomes available
    // Increased to 5 seconds to allow for slower-loading translations on production (e.g., Korean, Japanese)
    const timeoutId = setTimeout(() => {
      i18n.off('loaded', handleLoaded)
      clearInterval(pollInterval)
      // Final attempt before giving up
      if (urlLocale && i18n.language !== urlLocale) {
        console.warn(`[LanguageSyncProvider] Timeout waiting for language "${urlLocale}". Current: "${i18n.language}"`)
      }
      setIsLanguageSynced(true)
    }, 5000)

    return () => {
      i18n.off('loaded', handleLoaded)
      clearInterval(pollInterval)
      clearTimeout(timeoutId)
    }
  }, [i18n, currentPathname])

  // Don't render children until language is synced with URL to avoid showing wrong translations,
  // but provide an accessible loading indicator for screen readers
  if (!isLanguageSynced) {
    return (
      <output
        aria-live="polite"
        aria-busy="true"
        style={{ position: 'absolute', insetInlineStart: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}
      >
        Loading translations…
      </output>
    )
  }

  return children
}

export default LanguageSyncProvider
